import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ShieldCheck,
  Upload,
  Loader2,
  Trash2,
  KeyRound,
  BadgeCheck,
  XCircle,
  Clock,
  Lock,
  Eye,
  EyeOff,
  User as UserIcon,
  CreditCard,
  Sparkles,
  Crown,
  Rocket,
  Zap,
  Check,
  ArrowRight,
  Wallet as WalletIcon,
  Bell as BellIcon,
  CalendarClock,


} from "lucide-react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FreelancerAvatar } from "@/components/FreelancerAvatar";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { verifyPasswordChangeOtp } from "@/lib/password-otp.functions";
import { requestAccountDeletionOtp, confirmAccountDeletion } from "@/lib/delete-account.functions";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { PLANS, planFor, effectiveTier } from "@/lib/plans";
import { PayoutSettingsCard } from "@/components/PayoutSettingsCard";
import { NotificationsCard } from "@/components/settings/NotificationsCard";
import { TurnstileGate, type TurnstileGateHandle, captchaRequiredMessage } from "@/components/TurnstileGate";
import { useT } from "@/i18n/useT";


export const Route = createFileRoute("/_authenticated/settings")({
  head: () => ({
    meta: [{ title: "Settings — Lumvio" }, { name: "robots", content: "noindex" }],
  }),
  component: SettingsPage,
});

type TabId = "profile" | "security" | "notifications" | "billing" | "payouts" | "identity";

function SettingsPage() {
  const { t } = useTranslation();
  const tt = useT();
  const { profile } = useAuth();
  const isFreelancer = profile?.role === "freelancer";

  const tabs = useMemo(() => {
    const base: { id: TabId; label: string; icon: typeof UserIcon }[] = [
      { id: "profile", label: tt("Profile", "الملف"), icon: UserIcon },
      { id: "security", label: tt("Security", "الأمان"), icon: KeyRound },
      { id: "notifications", label: tt("Notifications", "الإشعارات"), icon: BellIcon },
    ];
    if (isFreelancer) {
      base.push(
        { id: "billing", label: tt("Billing", "الفوترة"), icon: CreditCard },
        { id: "payouts", label: tt("Payouts", "المدفوعات"), icon: WalletIcon },
        { id: "identity", label: tt("Identity", "الهوية"), icon: ShieldCheck },
      );
    }
    return base;
  }, [isFreelancer, tt]);

  const [tab, setTab] = useState<TabId>(() => {
    if (typeof window === "undefined") return "profile";
    const h = window.location.hash.replace("#", "") as TabId;
    return (tabs.find((x) => x.id === h)?.id ?? "profile") as TabId;
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      history.replaceState(null, "", `#${tab}`);
    }
  }, [tab]);

  return (
    <div className="mx-auto max-w-3xl space-y-10">
      {/* Apple-style hero header */}
      <header className="relative isolate overflow-hidden rounded-[28px] border border-border/60 bg-gradient-to-b from-card to-card/40 px-6 py-8 shadow-[0_1px_0_rgba(255,255,255,0.05)_inset,0_20px_60px_-30px_rgba(0,0,0,0.6)] sm:px-8 sm:py-10">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
        <div className="pointer-events-none absolute -top-24 right-[-10%] h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative space-y-2">
          <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            <span className="h-1 w-1 rounded-full bg-primary" aria-hidden />
            {tt("Account", "الحساب")}
          </span>
          <h1 className="text-[34px] font-semibold leading-[1.05] tracking-[-0.02em] sm:text-[40px]">
            {t("settings.title")}
          </h1>
          <p className="max-w-xl text-[15px] leading-relaxed text-muted-foreground">
            {t("settings.subtitle")}
          </p>
        </div>
      </header>

      {/* Apple-style segmented tab bar */}
      <div className="relative -mx-1 overflow-x-auto scrollbar-none">
        <div className="inline-flex items-center gap-0.5 rounded-full border border-border/60 bg-gradient-to-b from-muted/40 to-muted/10 p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_1px_2px_rgba(0,0,0,0.25)] backdrop-blur-xl">
          {tabs.map(({ id, label, icon: Icon }, i) => {
            const active = tab === id;
            const prevActive = i > 0 && tabs[i - 1].id === tab;
            return (
              <div key={id} className="flex items-center">
                {i > 0 && !active && !prevActive && (
                  <span className="mx-0.5 h-4 w-px bg-border/60" aria-hidden />
                )}
                <button
                  type="button"
                  onClick={() => setTab(id)}
                  aria-pressed={active}
                  className={cn(
                    "relative inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[13px] font-medium tracking-tight transition-all duration-200",
                    active
                      ? "bg-gradient-to-b from-background to-background/80 text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.15),0_1px_2px_rgba(0,0,0,0.15),0_4px_12px_-4px_rgba(0,0,0,0.35)]"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Icon className={cn("h-3.5 w-3.5 transition-all", active && "text-primary")} strokeWidth={active ? 2.5 : 2} />
                  {label}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div key={tab} className="space-y-6 [animation:fade-in_0.35s_ease-out]">
        {tab === "profile" && (
          <>
            <ProfilePhotoCard />
            <FullNameCard />
          </>
        )}

        {tab === "security" && (
          <>
            <ChangePasswordCard />
            <NotificationSoundCard />
            <DeleteAccountCard />
          </>
        )}

        {tab === "notifications" && <NotificationsCard />}

        {tab === "billing" && isFreelancer && <BillingCard />}

        {tab === "payouts" && isFreelancer && <PayoutSettingsCard />}

        {tab === "identity" && isFreelancer && (
          <KycStatusCard status={profile?.kyc_status ?? "pending"} duplicate={!!profile?.kyc_duplicate} />
        )}
      </div>
    </div>
  );
}

const PLAN_ICONS: Record<string, typeof Sparkles> = {
  free: Sparkles,
  starter: Rocket,
  pro: Zap,
  unlimited: Crown,
};

function BillingCard() {
  const { profile } = useAuth();
  const t = useT();
  const { t: tr, i18n } = useTranslation();
  const tier = effectiveTier(profile);
  const currentPlan = planFor(tier);
  const used = profile?.projects_created_this_month ?? 0;
  const bonus = profile?.projects_bonus_slots ?? 0;
  const limit = currentPlan.limit === null ? null : currentPlan.limit + bonus;
  const CurrentIcon = PLAN_ICONS[tier] ?? Sparkles;
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const upgradeOptions = PLANS.filter((p) => p.price > currentPlan.price);
  const rawFeatures = tr(`pricingPage.plans.${tier}.features`, { returnObjects: true, defaultValue: [] as string[] });
  const currentPlanFeatures: string[] = Array.isArray(rawFeatures) ? (rawFeatures as string[]) : [];
  const periodEnd = profile?.subscription_current_period_end ?? null;
  const subStatus = profile?.subscription_status ?? null;
  const isPaid = tier !== "free" && !!periodEnd;
  const lang = i18n.language?.startsWith("ar") ? "ar" : "en-US";
  const locale = lang;
  const endDate = periodEnd ? new Date(periodEnd) : null;
  const daysLeft = endDate ? Math.max(0, Math.ceil((endDate.getTime() - Date.now()) / 86400000)) : 0;
  const totalDays = 30;
  const renewPct = endDate ? Math.min(100, Math.max(0, Math.round(((totalDays - daysLeft) / totalDays) * 100))) : 0;
  const formattedDate = endDate
    ? endDate.toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" })
    : "";
  const statusLabel =
    subStatus === "active" || subStatus === "trialing"
      ? t("Active", "نشط")
      : subStatus === "past_due"
        ? t("Past due", "متأخر السداد")
        : subStatus === "canceled"
          ? t("Canceled", "ملغى")
          : t("Active", "نشط");

  return (
    <div className="space-y-6">
      {/* Current plan header */}
      <div className="relative overflow-hidden rounded-2xl border bg-card p-6 shadow-[var(--shadow-card)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-[shine_7s_ease-in-out_infinite]" />

        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 space-y-2">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.16em] text-primary">
              {t("Current plan", "الخطة الحالية")}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/25">
                <CurrentIcon className="h-5 w-5" />
              </div>
              <div>
                <div className="text-2xl font-semibold tracking-tight">{tr(`pricingPage.plans.${tier}.name`)}</div>
                <div className="text-sm text-muted-foreground">{tr(`pricingPage.plans.${tier}.tagline`)}</div>
              </div>
            </div>
            <div className="flex items-baseline gap-1 pt-2">
              <span className="text-3xl font-semibold tabular-nums">${currentPlan.price}</span>
              <span className="text-sm text-muted-foreground">
                {currentPlan.price === 0 ? t("one time", "مرة واحدة") : t("/mo", "/شهر")}
              </span>
            </div>
          </div>

          <Button asChild size="lg" className="shadow-[0_10px_30px_-10px_color-mix(in_oklch,var(--primary)_60%,transparent)]">
            <Link to="/plans">
              <Sparkles className="h-4 w-4" />
              {tier === "unlimited" ? t("Manage plan", "إدارة الخطة") : t("Upgrade plan", "ترقية الخطة")}
            </Link>
          </Button>
        </div>

        {/* Usage meter */}
        <div className="relative mt-6 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{t("Projects used this month", "المشاريع المستخدمة هذا الشهر")}</span>
            <span className="font-medium tabular-nums">
              {used} / {limit ?? "∞"}
            </span>
          </div>
          {limit ? (
            <div
              className="flex items-center gap-1.5"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={limit}
              aria-valuenow={used}
            >
              {Array.from({ length: Math.min(limit, 30) }).map((_, i) => {
                const filled = i < used;
                const atCap = used >= limit;
                return (
                  <span
                    key={i}
                    className={cn(
                      "h-2 flex-1 rounded-full transition-all duration-500",
                      filled
                        ? atCap
                          ? "bg-destructive shadow-[0_0_8px_color-mix(in_oklch,var(--destructive)_60%,transparent)]"
                          : "bg-gradient-to-r from-primary to-primary/70 shadow-[0_0_8px_color-mix(in_oklch,var(--primary)_50%,transparent)]"
                        : "bg-muted/60",
                    )}
                    style={{ transitionDelay: `${i * 60}ms` }}
                  />
                );
              })}
              {limit > 30 && (
                <span className="ms-1 text-[11px] text-muted-foreground tabular-nums">+{limit - 30}</span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              {Array.from({ length: 12 }).map((_, i) => (
                <span
                  key={i}
                  className="h-2 flex-1 rounded-full bg-gradient-to-r from-primary/70 to-primary/30 animate-pulse"
                  style={{ animationDelay: `${i * 90}ms` }}
                />
              ))}
            </div>
          )}
          {limit && pct >= 80 && (
            <p className="text-xs text-amber-500">
              {pct >= 100
                ? t("You've hit your monthly limit. Upgrade to keep creating projects.", "لقد وصلت إلى الحد الشهري. رقّ خطتك لمتابعة إنشاء المشاريع.")
                : t("You're close to your monthly limit.", "أنت قريب من الحد الشهري.")}
            </p>
          )}
        </div>

        {/* Feature list */}
        <ul className="relative mt-6 grid gap-2 text-sm sm:grid-cols-2">
          {currentPlanFeatures.map((f) => (
            <li key={f} className="flex items-start gap-2">
              <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
              <span className="text-muted-foreground">{f}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Renewal / billing cycle */}
      {isPaid && (
        <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-card to-card/60 p-6 shadow-[var(--shadow-card)]">
          <div className="pointer-events-none absolute -bottom-24 -right-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
          <div className="relative flex flex-wrap items-start justify-between gap-6">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/25">
                <CalendarClock className="h-5 w-5" />
              </div>
              <div className="space-y-1">
                <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.16em] text-emerald-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  {statusLabel}
                </div>
                <div className="text-sm text-muted-foreground">
                  {subStatus === "canceled"
                    ? t("Your plan ends on", "تنتهي خطتك في")
                    : t("Next billing date", "تاريخ الفوترة القادم")}
                </div>
                <div className="text-2xl font-semibold tracking-tight">{formattedDate}</div>
              </div>
            </div>
            <div className="text-right rtl:text-left">
              <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                {t("Renews in", "يتجدد خلال")}
              </div>
              <div className="mt-1 flex items-baseline gap-1 justify-end rtl:justify-start">
                <span className="text-4xl font-semibold tabular-nums text-primary">{daysLeft}</span>
                <span className="text-sm text-muted-foreground">{t("days", "يوم")}</span>
              </div>
            </div>
          </div>

          {/* Cycle progress bar with tick marks */}
          <div className="relative mt-6">
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted/60">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary via-primary/80 to-primary/60 transition-all duration-700"
                style={{ width: `${Math.max(2, renewPct)}%` }}
              />
            </div>
            <div className="mt-2 flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
              <span>{t("Start of cycle", "بداية الدورة")}</span>
              <span>{t("Renewal", "التجديد")}</span>
            </div>
          </div>
        </div>
      )}



      {/* Available upgrades */}
      {upgradeOptions.length > 0 && (
        <div className="rounded-2xl border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">{t("Available upgrades", "الترقيات المتاحة")}</h3>
              <p className="text-sm text-muted-foreground">{t("Unlock more projects and premium features.", "افتح المزيد من المشاريع والميزات المميزة.")}</p>
            </div>
            <Button asChild variant="ghost" size="sm">
              <Link to="/plans">
                {t("Compare plans", "قارن الخطط")} <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
              </Link>
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {upgradeOptions.map((p) => {
              const Icon = PLAN_ICONS[p.id] ?? Sparkles;
              return (
                <Link
                  key={p.id}
                  to="/plans"
                  className={cn(
                    "group relative flex flex-col overflow-hidden rounded-xl border bg-card/60 p-4 transition-all hover:-translate-y-0.5 hover:border-primary/40",
                    p.highlight && "border-primary/40",
                  )}
                >
                  {p.highlight && (
                    <span className="absolute right-3 top-3 rounded-full bg-primary px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-primary-foreground">
                      {t("Popular", "الأكثر رواجًا")}
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="font-medium">{tr(`pricingPage.plans.${p.id}.name`)}</div>
                  </div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-2xl font-semibold tabular-nums">${p.price}</span>
                    <span className="text-xs text-muted-foreground">{t("/mo", "/شهر")}</span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {p.limit ? t(`${p.limit} projects / month`, `${p.limit} مشاريع / شهر`) : t("Unlimited projects", "مشاريع غير محدودة")}
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary opacity-70 group-hover:opacity-100">
                    {t("Upgrade", "ترقية")} <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5 rtl:rotate-180" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-xl border bg-muted/30 p-4 text-xs text-muted-foreground">
        {t("Billed monthly via Stripe. Cancel anytime from your Stripe billing portal. Prices in USD.", "الفوترة شهريًا عبر Stripe. يمكنك الإلغاء في أي وقت من بوابة الفوترة. الأسعار بالدولار الأمريكي.")}
      </div>
    </div>
  );
}


function ProfilePhotoCard() {
  const { t } = useTranslation();
  const tt = useT();
  const { user, profile, refreshProfile } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const verified = profile?.kyc_status === "verified";

  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (!user) throw new Error(tt("Not signed in", "غير مسجّل الدخول"));
      if (!file.type.startsWith("image/")) throw new Error(tt("Please pick an image file", "الرجاء اختيار ملف صورة"));
      if (file.size > 5 * 1024 * 1024) throw new Error(tt("Image must be under 5 MB", "يجب أن تكون الصورة أقل من 5 ميغابايت"));
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `${user.id}/avatar-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("avatars")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      if (profile?.avatar_url && profile.avatar_url !== path) {
        await supabase.storage.from("avatars").remove([profile.avatar_url]);
      }
      const { error: updErr } = await supabase.from("profiles").update({ avatar_url: path }).eq("id", user.id);
      if (updErr) throw updErr;
    },
    onSuccess: async () => {
      toast.success(tt("Profile photo updated", "تم تحديث الصورة الشخصية"));
      await refreshProfile();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!user || !profile?.avatar_url) return;
      await supabase.storage.from("avatars").remove([profile.avatar_url]);
      const { error } = await supabase.from("profiles").update({ avatar_url: null }).eq("id", user.id);
      if (error) throw error;
    },
    onSuccess: async () => {
      toast.success(tt("Photo removed", "تمت إزالة الصورة"));
      await refreshProfile();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="rounded-2xl border border-border/60 bg-gradient-to-b from-card to-card/50 p-6 shadow-[0_1px_0_rgba(255,255,255,0.05)_inset,0_10px_30px_-20px_rgba(0,0,0,0.5)]">
      <div className="flex flex-wrap items-center gap-5">
        <FreelancerAvatar
          path={profile?.avatar_url}
          name={profile?.full_name ?? profile?.email}
          verified={verified}
          size="xl"
        />
        <div className="min-w-0 flex-1">
          <h2 className="font-medium">{t("settings.profilePhoto")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("settings.profilePhotoHelp")}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
                e.target.value = "";
              }}
            />
            <Button size="sm" onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
              {upload.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> {t("settings.uploading")}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />{" "}
                  {profile?.avatar_url ? t("settings.changePhoto") : t("settings.uploadPhoto")}
                </>
              )}
            </Button>
            {profile?.avatar_url && (
              <Button size="sm" variant="ghost" onClick={() => remove.mutate()} disabled={remove.isPending}>
                <Trash2 className="h-4 w-4" /> {t("settings.remove")}
              </Button>
            )}
          </div>
          {!verified && profile?.role === "freelancer" && (
            <p className="mt-3 text-xs text-muted-foreground">
              {tt("Not verified yet — complete identity verification to earn the verified badge.", "لم يتم التحقق بعد — أكمل التحقق من الهوية للحصول على شارة الموثّق.")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function FullNameCard() {
  const { t } = useTranslation();
  const tt = useT();
  const { user, profile, refreshProfile } = useAuth();
  const verified = profile?.kyc_status === "verified";
  const [name, setName] = useState(profile?.full_name ?? "");

  useEffect(() => {
    setName(profile?.full_name ?? "");
  }, [profile?.full_name]);

  const save = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim();
      if (trimmed.length < 2) throw new Error(tt("Please enter your full name", "الرجاء إدخال اسمك الكامل"));
      if (trimmed.length > 80) throw new Error(tt("Name is too long", "الاسم طويل جدًا"));
      const { error } = await supabase.from("profiles").update({ full_name: trimmed }).eq("id", user!.id);
      if (error) {
        if (error.message?.includes("NAME_LOCKED")) {
          throw new Error(tt("Your name is locked because your identity is verified. Contact support to change it.", "اسمك مقفل لأن هويتك موثّقة. تواصل مع الدعم لتغييره."));
        }
        throw error;
      }
    },
    onSuccess: async () => {
      toast.success(tt("Name updated", "تم تحديث الاسم"));
      await refreshProfile();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const dirty = (name.trim() ?? "") !== (profile?.full_name ?? "");

  return (
    <div className="rounded-2xl border border-border/60 bg-gradient-to-b from-card to-card/50 p-6 shadow-[0_1px_0_rgba(255,255,255,0.05)_inset,0_10px_30px_-20px_rgba(0,0,0,0.5)] space-y-5">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-primary/10 p-2 text-primary">
          <UserIcon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-medium">{t("settings.fullName")}</h2>
            {verified && (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <Lock className="h-3 w-3" /> {t("settings.locked")}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {verified ? t("settings.fullNameLockedWhen") : t("settings.fullNameHelp")}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="full-name">{t("settings.name")}</Label>
        <Input
          id="full-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={verified}
          placeholder={tt("e.g. Alex Morgan", "مثال: علي أحمد")}
          autoComplete="name"
        />
      </div>

      {!verified && (
        <div className="flex justify-end">
          <Button onClick={() => save.mutate()} disabled={save.isPending || !dirty || !name.trim()}>
            {save.isPending ? t("settings.saving") : t("settings.saveName")}
          </Button>
        </div>
      )}
    </div>
  );
}

function ChangePasswordCard() {
  const { t, i18n } = useTranslation();
  const tt = useT();
  const { user } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [code, setCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const verifyPwOtp = useServerFn(verifyPasswordChangeOtp);
  const captchaRef = useRef<TurnstileGateHandle>(null);

  const sendOtp = useMutation({
    mutationFn: async () => {
      if (pw.length < 8) throw new Error(tt("Password must be at least 8 characters", "يجب أن تكون كلمة المرور 8 أحرف على الأقل"));
      if (pw !== confirm) throw new Error(tt("Passwords do not match", "كلمتا المرور غير متطابقتين"));
      const email = user?.email;
      if (!email) throw new Error(tt("No email on account.", "لا يوجد بريد إلكتروني في الحساب."));
      const captchaToken = (await captchaRef.current?.ensureToken()) ?? undefined;
      if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken) {
        throw new Error(captchaRequiredMessage(t));
      }
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: false, captchaToken },
      });
      captchaRef.current?.reset();
      if (error) throw error;
    },

    onSuccess: () => {
      setOtpSent(true);
      toast.success(tt("We sent an 8-digit code to your email", "أرسلنا رمزًا مكوّنًا من 8 أرقام إلى بريدك"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const change = useMutation({
    mutationFn: async () => {
      if (!/^\d{8}$/.test(code)) throw new Error(tt("Enter the 8-digit code", "أدخل الرمز المكوّن من 8 أرقام"));
      await verifyPwOtp({ data: { token: code, new_password: pw } });
    },
    onSuccess: () => {
      toast.success(tt("Password updated", "تم تحديث كلمة المرور"));
      setPw("");
      setConfirm("");
      setCode("");
      setOtpSent(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="rounded-2xl border border-border/60 bg-gradient-to-b from-card to-card/50 p-6 shadow-[0_1px_0_rgba(255,255,255,0.05)_inset,0_10px_30px_-20px_rgba(0,0,0,0.5)] space-y-5">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-primary/10 p-2 text-primary">
          <KeyRound className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-medium">{t("settings.changePassword")}</h2>
          <p className="text-sm text-muted-foreground">
            {tt("For your security, we'll email an 8-digit code to confirm this change.", "لأمانك، سنرسل رمزًا مكوّنًا من 8 أرقام إلى بريدك لتأكيد هذا التغيير.")}
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="new-pw">{t("settings.newPassword")}</Label>
          <div className="relative">
            <Input
              id="new-pw"
              type={showPw ? "text" : "password"}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoComplete="new-password"
              placeholder={t("settings.atLeast8")}
              disabled={otpSent}
              className={i18n.dir() === "rtl" ? "ps-10" : "pe-10"}
            />
            <button
              type="button"
              aria-label={showPw ? t("auth.hidePassword") : t("auth.showPassword")}
              onClick={() => setShowPw((v) => !v)}
              disabled={otpSent}
              className={`absolute top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-50 ${i18n.dir() === "rtl" ? "left-2" : "right-2"}`}
            >
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-pw">{t("settings.confirmPassword")}</Label>
          <div className="relative">
            <Input
              id="confirm-pw"
              type={showPw ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              placeholder={t("settings.repeatNewPassword")}
              disabled={otpSent}
              className={i18n.dir() === "rtl" ? "ps-10" : "pe-10"}
            />
            <button
              type="button"
              aria-label={showPw ? t("auth.hidePassword") : t("auth.showPassword")}
              onClick={() => setShowPw((v) => !v)}
              disabled={otpSent}
              className={`absolute top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-50 ${i18n.dir() === "rtl" ? "left-2" : "right-2"}`}
            >
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {!otpSent ? (
        <div className="space-y-3">
          <TurnstileGate ref={captchaRef} action="password-change" />
          <div className="flex justify-end">
            <Button
              onClick={() => sendOtp.mutate()}
              disabled={sendOtp.isPending || !pw || !confirm}
            >
              {sendOtp.isPending ? tt("Sending code…", "جارٍ الإرسال…") : tt("Send verification code", "إرسال رمز التحقق")}
            </Button>
          </div>
        </div>

      ) : (
        <div className="space-y-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
          <Label>{tt("Enter the 8-digit code sent to", "أدخل الرمز المكوّن من 8 أرقام المرسل إلى")} {user?.email}</Label>
          <InputOTP maxLength={8} value={code} onChange={setCode}>
            <InputOTPGroup>
              {Array.from({ length: 8 }).map((_, i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setOtpSent(false);
                setCode("");
              }}
              disabled={change.isPending}
            >
              {tt("Cancel", "إلغاء")}
            </Button>
            <Button
              variant="outline"
              onClick={() => sendOtp.mutate()}
              disabled={sendOtp.isPending || change.isPending}
            >
              {tt("Resend code", "إعادة إرسال الرمز")}
            </Button>
            <Button
              onClick={() => change.mutate()}
              disabled={change.isPending || code.length !== 8}
            >
              {change.isPending ? t("settings.updating") : t("settings.updatePassword")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function KycStatusCard({
  status,
  duplicate,
}: {
  status: "not_started" | "pending" | "verified" | "rejected";
  duplicate: boolean;
}) {
  const { t } = useTranslation();
  const tt = useT();
  const config =
    status === "verified"
      ? {
          Icon: BadgeCheck,
          tone: "text-emerald-600 dark:text-emerald-400",
          bg: "bg-emerald-500/10",
          border: "border-emerald-500/30",
          label: tt("Verified", "موثّق"),
          message: tt("Your identity is confirmed. You can create projects and receive payouts.", "تم تأكيد هويتك. يمكنك إنشاء المشاريع واستلام المدفوعات."),
          action: null as null | { label: string; to: string; external?: boolean; variant?: "outline" | "default" },
        }
      : status === "rejected" && duplicate
        ? {
            Icon: XCircle,
            tone: "text-destructive",
            bg: "bg-destructive/10",
            border: "border-destructive/30",
            label: tt("Refused — duplicate ID", "مرفوض — هوية مكرّرة"),
            message: tt(
              "This ID document is already verified on another Lumvio account. Sign in to that account, or contact support to release the identity.",
              "هذه الهوية موثّقة بالفعل على حساب آخر في لومڤيو. سجّل الدخول إلى ذلك الحساب أو تواصل مع الدعم لتحرير الهوية.",
            ),
            action: {
              label: tt("Contact support", "تواصل مع الدعم"),
              to: "mailto:contact@lumvio.co?subject=KYC%20duplicate%20release",
              external: true,
            },
          }
        : status === "rejected"
          ? {
              Icon: XCircle,
              tone: "text-destructive",
              bg: "bg-destructive/10",
              border: "border-destructive/30",
              label: tt("Rejected", "مرفوض"),
              message: tt(
                "Your last verification attempt didn't match. Retry with a clearer document photo and good lighting for the live capture.",
                "لم تتطابق محاولة التحقق الأخيرة. حاول مجددًا بصورة أوضح للوثيقة وإضاءة جيدة للالتقاط المباشر.",
              ),
              action: { label: tt("Try again", "حاول مجددًا"), to: "/verify" },
            }
          : {
              Icon: Clock,
              tone: "text-amber-500",
              bg: "bg-amber-500/10",
              border: "border-amber-500/30",
              label: tt("Pending", "قيد الانتظار"),
              message: tt("Verify your identity to unlock your free project and receive payouts.", "وثّق هويتك لفتح مشروعك المجاني واستلام المدفوعات."),
              action: { label: tt("Start verification", "ابدأ التحقق"), to: "/verify" },
            };

  const Icon = config.Icon;
  return (
    <div className={cn("rounded-2xl border border-border/60 bg-gradient-to-b from-card to-card/50 p-6 shadow-[0_1px_0_rgba(255,255,255,0.05)_inset,0_10px_30px_-20px_rgba(0,0,0,0.5)] space-y-4", config.border)}>
      <div className="flex items-start gap-3">
        <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full", config.bg)}>
          <Icon className={cn("h-5 w-5", config.tone)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-medium">{t("settings.identityVerification")}</h2>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                config.bg,
                config.tone,
              )}
            >
              {config.label}
            </span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{config.message}</p>
        </div>
      </div>
      {config.action && (
        <div className="flex justify-end">
          {config.action.external ? (
            <Button asChild variant={config.action.variant ?? "default"}>
              <a href={config.action.to}>{config.action.label}</a>
            </Button>
          ) : (
            <Button asChild variant={config.action.variant ?? "default"}>
              <Link to={config.action.to}>{config.action.label}</Link>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function NotificationSoundCard() {
  const tt = useT();
  const { user, profile, refreshProfile } = useAuth();
  const enabled = profile?.notification_sound_enabled ?? true;
  const [busy, setBusy] = useState(false);

  async function toggle(next: boolean) {
    if (!user) return;
    setBusy(true);
    const { error } = await supabase.from("profiles").update({ notification_sound_enabled: next }).eq("id", user.id);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    await refreshProfile();
    toast.success(next ? tt("Notification sound enabled", "تم تفعيل صوت الإشعارات") : tt("Notification sound muted", "تم كتم صوت الإشعارات"));
  }

  return (
    <div className="rounded-2xl border border-border/60 bg-gradient-to-b from-card to-card/50 p-6 shadow-[0_1px_0_rgba(255,255,255,0.05)_inset,0_10px_30px_-20px_rgba(0,0,0,0.5)]">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-medium">{tt("Notification sound", "صوت الإشعارات")}</h2>
          <p className="text-sm text-muted-foreground">
            {tt("Play a soft chime when a new message or important update arrives.", "تشغيل نغمة هادئة عند وصول رسالة جديدة أو تحديث مهم.")}
          </p>
        </div>
        <Switch checked={enabled} disabled={busy} onCheckedChange={toggle} />
      </div>
    </div>
  );
}


function DeleteAccountCard() {
  const tt = useT();
  const { user } = useAuth();
  const [step, setStep] = useState<"idle" | "confirm" | "code">("idle");
  const [ack, setAck] = useState("");
  const [code, setCode] = useState("");
  const [pinId, setPinId] = useState<string | null>(null);
  const sendCode = useServerFn(requestAccountDeletionOtp);
  const confirmDelete = useServerFn(confirmAccountDeletion);

  const reason = (e: Error) => {
    const m = e.message;
    if (m === "pending_withdrawal")
      return tt(
        "You have a withdrawal request in progress. Wait until it is paid before deleting your account.",
        "لديك طلب سحب قيد المعالجة. انتظر حتى يتم دفعه قبل حذف حسابك.",
      );
    if (m === "funds_held")
      return tt(
        "There is still money held for one of your projects. It must be released or refunded first.",
        "لا يزال هناك مال محتجز لأحد مشاريعك. يجب تحريره أو استرداده أولاً.",
      );
    if (m === "rate_limited")
      return tt("Too many codes requested. Try again later.", "تم طلب رموز كثيرة. حاول لاحقًا.");
    if (m === "invalid_code") return tt("Wrong code.", "رمز غير صحيح.");
    if (m === "expired") return tt("The code expired. Send a new one.", "انتهت صلاحية الرمز. أرسل رمزًا جديدًا.");
    if (m === "too_many_attempts")
      return tt("Too many wrong attempts. Send a new code.", "محاولات خاطئة كثيرة. أرسل رمزًا جديدًا.");
    return m;
  };

  const send = useMutation({
    mutationFn: async () => await sendCode({}),
    onSuccess: (res: { pinId: string }) => {
      setPinId(res.pinId);
      setCode("");
      setStep("code");
      toast.success(tt("We sent a 6-digit code to your email", "أرسلنا رمزًا مكوّنًا من 6 أرقام إلى بريدك"));
    },
    onError: (e: Error) => toast.error(reason(e)),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!pinId) throw new Error("invalid_code");
      await confirmDelete({ data: { pinId, code } });
    },
    onSuccess: async () => {
      toast.success(tt("Your account has been deleted", "تم حذف حسابك"));
      try {
        await supabase.auth.signOut();
      } catch {
        /* ignore */
      }
      window.location.href = "/";
    },
    onError: (e: Error) => toast.error(reason(e)),
  });

  const confirmWord = tt("DELETE", "حذف");

  return (
    <div className="rounded-2xl border border-destructive/30 bg-gradient-to-b from-destructive/[0.06] to-card/40 p-6 space-y-5">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-destructive/10 p-2 text-destructive">
          <Trash2 className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-medium">{tt("Delete account", "حذف الحساب")}</h2>
          <p className="text-sm text-muted-foreground">
            {tt(
              "This permanently removes your profile, projects, messages and files. It cannot be undone. We email a code to confirm it's really you.",
              "سيؤدي هذا إلى إزالة ملفك ومشاريعك ورسائلك وملفاتك نهائيًا. لا يمكن التراجع. سنرسل رمزًا إلى بريدك للتأكد أنه أنت.",
            )}
          </p>
        </div>
      </div>

      {step === "idle" && (
        <div className="flex justify-end">
          <Button variant="destructive" onClick={() => setStep("confirm")}>
            {tt("Delete my account", "حذف حسابي")}
          </Button>
        </div>
      )}

      {step === "confirm" && (
        <div className="space-y-3 rounded-lg border border-destructive/25 bg-destructive/5 p-4">
          <Label htmlFor="del-ack">
            {tt("Type", "اكتب")} <span className="font-semibold">{confirmWord}</span>{" "}
            {tt("to continue", "للمتابعة")}
          </Label>
          <Input id="del-ack" value={ack} onChange={(e) => setAck(e.target.value)} autoComplete="off" />
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setStep("idle");
                setAck("");
              }}
            >
              {tt("Cancel", "إلغاء")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => send.mutate()}
              disabled={send.isPending || ack.trim().toUpperCase() !== confirmWord}
            >
              {send.isPending ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  {tt("Sending code…", "جارٍ الإرسال…")}
                </>
              ) : (
                tt("Email me a code", "أرسل لي رمزًا")
              )}
            </Button>
          </div>
        </div>
      )}

      {step === "code" && (
        <div className="space-y-3 rounded-lg border border-destructive/25 bg-destructive/5 p-4">
          <Label>
            {tt("Enter the 6-digit code sent to", "أدخل الرمز المكوّن من 6 أرقام المرسل إلى")} {user?.email}
          </Label>
          <InputOTP maxLength={6} value={code} onChange={setCode}>
            <InputOTPGroup>
              {Array.from({ length: 6 }).map((_, i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setStep("idle");
                setAck("");
                setCode("");
                setPinId(null);
              }}
              disabled={remove.isPending}
            >
              {tt("Cancel", "إلغاء")}
            </Button>
            <Button variant="outline" onClick={() => send.mutate()} disabled={send.isPending || remove.isPending}>
              {tt("Resend code", "إعادة إرسال الرمز")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => remove.mutate()}
              disabled={remove.isPending || code.length !== 6}
            >
              {remove.isPending ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  {tt("Deleting…", "جارٍ الحذف…")}
                </>
              ) : (
                tt("Permanently delete", "حذف نهائي")
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
